import {DatePipe} from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostBinding,
  inject,
  input,
  InputSignal,
  OnInit,
  Signal,
  viewChildren
} from '@angular/core';
import {Router} from '@angular/router';
import {DsMessageModule, DsToggleButtonModule} from '@bmw-ds/components';
import {TranslateModule} from '@ngx-translate/core';
import {Case, IAuthInfo, ICase, User} from '@service-and-repairs/awpintegrationlib';
import {environment} from '@environments/environment';
import {AuthInfoFactory} from '../../../../auth/auth-info-factory';
import {AuthenticationTokenBearerService} from '../../../../auth/authentication-token-bearer.service';
import {EtkConfiguration} from '../../../../core/configuration/interfaces/etk-configuration';
import {LeadsConfiguration} from '../../../../core/configuration/interfaces/leads-configuration';
import {OutletRelatedConfiguration} from '../../../../core/configuration/interfaces/outlet-related-configuration';
import {ConfigurationLoader} from '../../../../core/configuration/services/configuration.loader';
import {ConfigurationService} from '../../../../core/configuration/services/configuration.service';
import {ServiceCaseHolder} from '../../../../core/service-case/models/service-case-holder';
import {UserData} from '../../../../core/user/models/user-data';
import {UserService} from '../../../../core/user/services/user.service';
import {SERVICE_CASE_MAIN_SOURCE, Util} from '../../../../util/util';
import {ShoppingCartButtonComponent} from '../shopping-cart-button/shopping-cart-button.component';

export const LOCAL_STORAGE_KEY_FOR_SHORTLIST_PINNED_STATE = 'shortlistPinned';

@Component({
  selector: 'app-shortlist',
  templateUrl: './shortlist.component.html',
  styleUrls: ['./shortlist.component.scss'],
  imports: [ShoppingCartButtonComponent, DsToggleButtonModule, DsMessageModule, DatePipe, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShortlistComponent implements OnInit {
  configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);
  serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly router: Router = inject(Router);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService = inject(
    AuthenticationTokenBearerService);
  private readonly userService: UserService = inject(UserService);

  readonly shortlistViewState: InputSignal<{ visible: boolean }> = input({visible: false});

  @HostBinding('class.pinned')
  isPinned: boolean = true;

  readonly shortlistElementRefs: Signal<readonly ElementRef []> = viewChildren<ElementRef>('shortlistWebComponent');

  showErrorIndicator: boolean = false;
  serviceCaseFrontendLoaded: boolean = false;

  leadsConfiguration: LeadsConfiguration;
  readonly softPlanningBaseUrl: string = ConfigurationService.getUrlForPortalHost('softPlanning');
  readonly isSoftPlanningPreviewEnabled: boolean = environment.softPlanningPreviewEnabled;

  user: UserData;
  authInfo: IAuthInfo;
  headers: string;

  constructor() {
    this.userService.user$.subscribe((user: UserData): void => this.onUserChanged(user));
    this.onAuthTokenChanged();
    this.authenticationTokenBearerService.oidcTokenSubject.subscribe(this.onAuthTokenChanged.bind(this));
    this.authenticationTokenBearerService.csslTokenSubject.subscribe(this.onAuthTokenChanged.bind(this));
    this.configurationLoader.configChanged
      .subscribe((config: {
        outletRelatedConfig: OutletRelatedConfiguration,
        etkConfig: EtkConfiguration
      }): void => {
        this.leadsConfiguration = config.outletRelatedConfig?.leadsDealerConfiguration;
      });
    this.serviceCaseHolder.activeServiceCaseChanged.subscribe(() => this.onActiveCaseChanged());
  }

  ngOnInit(): void {
    this.loadServiceCaseFrontendSource();
    this.restorePinnedStateFromLocalStorage();
  }

  togglePinnedState(): void {
    this.isPinned = !this.isPinned;
    localStorage.setItem(LOCAL_STORAGE_KEY_FOR_SHORTLIST_PINNED_STATE, JSON.stringify(this.isPinned));
  }

  openServiceCaseDetails(): void {
    this.router.navigateByUrl('casedetails').then((): void => this.close());
  }

  close(): void {
    this.shortlistViewState().visible = false;
  }

  isHidden(serviceCase: ICase): boolean {
    return !serviceCase || (this.serviceCaseHolder.getActiveCase()?.getExternalId() !== serviceCase.getExternalId());
  }

  showFlatRateUnitAmount(): boolean {
    return this.configurationLoader.showFlatRateUnitValues();
  }

  showFullRetailSaving(): boolean {
    return this.configurationLoader.showFullRetailSaving();
  }

  isSoftPlanningEnabled(): boolean {
    return this.configurationLoader.isSoftPlanningEnabled();
  }

  private loadServiceCaseFrontendSource(): void {
    Util.attachScript(SERVICE_CASE_MAIN_SOURCE)
      .then((): void => {
        this.serviceCaseFrontendLoaded = true;
      })
      .catch((): void => {
        this.showErrorIndicator = true;
      });
  }

  private restorePinnedStateFromLocalStorage(): void {
    const storedPinnedState: string | null = localStorage.getItem(LOCAL_STORAGE_KEY_FOR_SHORTLIST_PINNED_STATE);
    // Keep the default (pinned) unless the user explicitly unpinned the shortlist before
    this.isPinned = storedPinnedState !== JSON.stringify(false);
  }

  private onAuthTokenChanged(): void {
    if (this.user) {
      this.authInfo = AuthInfoFactory.makeAuthInfo(this.user, this.serviceCaseHolder.getActiveCase()?.getBrand());
    } else {
      this.authInfo = undefined;
    }
    this.headers = JSON.stringify(this.authenticationTokenBearerService.oidcTokenSubject.getValue()?.getHeaders());
  }

  private onActiveCaseChanged(): void {
    // recreate active case on respective shortlist instance to trigger change detection
    const activeCaseId: string = this.serviceCaseHolder.getActiveCase()?.getExternalId();
    if (activeCaseId) {
      this.shortlistElementRefs().forEach((elementRef: ElementRef): void => {
        // Angular 21 elements expose signal inputs as plain values, older versions expose the signal itself
        const activeCaseInput: ICase | (() => ICase) = elementRef.nativeElement['activeCase'];
        const shortlistCase: ICase = typeof activeCaseInput === 'function' ? activeCaseInput() : activeCaseInput;
        if (shortlistCase?.getExternalId() === activeCaseId) {
          elementRef.nativeElement['activeCase'] =
            Case.constructFromPlainObject(this.serviceCaseHolder.getActiveCase());
        }
      });
    }
  }

  private onUserChanged(user: UserData): void {
    this.user = user;
    // recreate user on respective shortlist instance to trigger change detection
    if (this.user) {
      this.shortlistElementRefs().forEach((elementRef: ElementRef): void => {
        elementRef.nativeElement['userData'] = User.constructFromPlainObject(this.user);
      });
    }
  }
}
